import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";
import ImageUploadField from "./ImageUploadField";

function arrayToLines(value) {
    if (!Array.isArray(value)) return "";
    return value.join("\n");
}

function linesToArray(value) {
    return value
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
}

function detailsToLines(value) {
    if (!Array.isArray(value)) return "";

    return value
        .map((item) => {
            if (!item?.label && !item?.value) return "";
            return `${item.label || ""}: ${item.value || ""}`;
        })
        .filter(Boolean)
        .join("\n");
}

function linesToDetails(value) {
    return value
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const [label, ...rest] = line.split(":");

            return {
                label: label?.trim() || "",
                value: rest.join(":").trim() || "",
            };
        })
        .filter((item) => item.label || item.value);
}

export default function AdminDogFormPage({
                                             primaryColor = "#8D6E63",
                                             secondaryColor = "#A1887F",
                                         }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const isEditing = Boolean(id);

    const [name, setName] = useState("");
    const [sex, setSex] = useState("Hündin");
    const [imageSrc, setImageSrc] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [description, setDescription] = useState("");
    const [badges, setBadges] = useState("");
    const [details, setDetails] = useState("");
    const [highlights, setHighlights] = useState("");
    const [isVisible, setIsVisible] = useState(true);
    const [showInDogsSection, setShowInDogsSection] = useState(true);
    const [isExternal, setIsExternal] = useState(false);
    const [sortOrder, setSortOrder] = useState(0);

    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!isEditing) return;

        async function loadDog() {
            setIsLoading(true);
            setErrorMessage("");

            const { data, error } = await supabase
                .from("dogs")
                .select("*")
                .eq("id", id)
                .single();

            if (error) {
                console.error("Fehler beim Laden des Hundes:", error);
                setErrorMessage("Der Hund konnte nicht geladen werden.");
                setIsLoading(false);
                return;
            }

            setName(data.name || "");
            setSex(data.sex || "Hündin");
            setImageSrc(data.image_src || "");
            setSubtitle(data.subtitle || "");
            setDescription(data.description || "");
            setBadges(arrayToLines(data.badges));
            setDetails(detailsToLines(data.details));
            setHighlights(arrayToLines(data.highlights));
            setIsVisible(data.is_visible !== false);
            setShowInDogsSection(data.show_in_dogs_section !== false);
            setIsExternal(Boolean(data.is_external));
            setSortOrder(data.sort_order ?? 0);

            setIsLoading(false);
        }

        loadDog();
    }, [id, isEditing]);

    async function handleSubmit(event) {
        event.preventDefault();

        if (!name.trim()) {
            setErrorMessage("Bitte gib einen Namen ein.");
            return;
        }

        if (!sex) {
            setErrorMessage("Bitte wähle ein Geschlecht aus.");
            return;
        }

        setIsSaving(true);
        setErrorMessage("");

        const payload = {
            name: name.trim(),
            sex,
            image_src: imageSrc.trim() || null,
            subtitle: subtitle.trim() || null,
            description: description.trim() || null,
            badges: linesToArray(badges),
            details: linesToDetails(details),
            highlights: linesToArray(highlights),
            is_visible: isVisible,
            show_in_dogs_section: showInDogsSection,
            is_external: isExternal,
            sort_order: Number(sortOrder) || 0,
            updated_at: new Date().toISOString(),
        };

        if (isEditing) {
            const { error } = await supabase
                .from("dogs")
                .update(payload)
                .eq("id", id);

            setIsSaving(false);

            if (error) {
                console.error("Fehler beim Speichern:", error);
                setErrorMessage("Der Hund konnte nicht gespeichert werden.");
                return;
            }

            navigate("/admin/dogs");
            return;
        }

        const { error } = await supabase.from("dogs").insert([
            {
                ...payload,
                created_at: new Date().toISOString(),
            },
        ]);

        setIsSaving(false);

        if (error) {
            console.error("Fehler beim Erstellen:", error);
            setErrorMessage("Der Hund konnte nicht erstellt werden.");
            return;
        }

        navigate("/admin/dogs");
    }

    if (isLoading) {
        return (
            <main style={adminStyles.dashboardPage}>
                <div style={adminStyles.dashboardContainer}>
                    <p style={{ color: "#666" }}>Hund wird geladen...</p>
                </div>
            </main>
        );
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            {isEditing ? "Hund bearbeiten" : "Neuer Hund"}
                        </h1>
                        <p style={adminStyles.subtitle}>
                            Pflege hier die Daten für eigene Zuchthunde und externe Elterntiere.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/admin/dogs")}
                        style={{
                            ...adminStyles.secondaryButton,
                            backgroundColor: secondaryColor,
                        }}
                    >
                        Zurück
                    </button>
                </header>

                <motion.form
                    onSubmit={handleSubmit}
                    style={{
                        ...adminStyles.dashboardCard,
                        maxWidth: "900px",
                    }}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                            gap: "1rem",
                        }}
                    >
                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="name">
                                Name
                            </label>
                            <input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                style={adminStyles.input}
                                placeholder="z. B. Kira oder Sarotti"
                            />
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="sex">
                                Geschlecht
                            </label>
                            <select
                                id="sex"
                                value={sex}
                                onChange={(event) => setSex(event.target.value)}
                                style={adminStyles.input}
                            >
                                <option value="Hündin">Hündin</option>
                                <option value="Rüde">Rüde</option>
                            </select>
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="sortOrder">
                                Sortierung
                            </label>
                            <input
                                id="sortOrder"
                                type="number"
                                value={sortOrder}
                                onChange={(event) => setSortOrder(event.target.value)}
                                style={adminStyles.input}
                            />
                        </div>
                    </div>

                    <ImageUploadField
                        label="Hundebild"
                        value={imageSrc}
                        onChange={setImageSrc}
                        folder="dogs"
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        previewAlt={name || "Hundebild"}
                    />

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="subtitle">
                            Untertitel
                        </label>
                        <input
                            id="subtitle"
                            type="text"
                            value={subtitle}
                            onChange={(event) => setSubtitle(event.target.value)}
                            style={adminStyles.input}
                            placeholder="z. B. Havaneser · weiß-zobel · 24,5 cm · 5,1 kg"
                        />
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="description">
                            Beschreibung
                        </label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "130px",
                                resize: "vertical",
                                lineHeight: 1.6,
                                fontFamily: "inherit",
                            }}
                            placeholder="Kurze Beschreibung des Hundes."
                        />
                    </div>

                    <div
                        style={{
                            backgroundColor: "#faf7f3",
                            border: "1px solid rgba(0,0,0,0.06)",
                            borderRadius: "18px",
                            padding: "1rem",
                            marginBottom: "1.25rem",
                        }}
                    >
                        <strong
                            style={{
                                display: "block",
                                color: primaryColor,
                                marginBottom: "0.85rem",
                            }}
                        >
                            Sichtbarkeit & Zuordnung
                        </strong>

                        <label
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "0.7rem",
                                marginBottom: "0.9rem",
                                cursor: "pointer",
                                color: "#444",
                                lineHeight: 1.5,
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={isVisible}
                                onChange={(event) => setIsVisible(event.target.checked)}
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    marginTop: "0.2rem",
                                    accentColor: primaryColor,
                                }}
                            />
                            <span>
                                <strong>Hund öffentlich nutzbar</strong>
                                <br />
                                <small>
                                    Der Hund kann auf der Webseite erscheinen, zum Beispiel als
                                    Elterntier eines Wurfs.
                                </small>
                            </span>
                        </label>

                        <label
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "0.7rem",
                                marginBottom: "0.9rem",
                                cursor: "pointer",
                                color: "#444",
                                lineHeight: 1.5,
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={showInDogsSection}
                                onChange={(event) =>
                                    setShowInDogsSection(event.target.checked)
                                }
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    marginTop: "0.2rem",
                                    accentColor: primaryColor,
                                }}
                            />
                            <span>
                                <strong>In „Meine Hündinnen & Rüden“ anzeigen</strong>
                                <br />
                                <small>
                                    Nur aktivieren, wenn der Hund wirklich zur eigenen Zucht
                                    gehört.
                                </small>
                            </span>
                        </label>

                        <label
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "0.7rem",
                                cursor: "pointer",
                                color: "#444",
                                lineHeight: 1.5,
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={isExternal}
                                onChange={(event) => setIsExternal(event.target.checked)}
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    marginTop: "0.2rem",
                                    accentColor: primaryColor,
                                }}
                            />
                            <span>
                                <strong>Externer Hund / Deckrüde</strong>
                                <br />
                                <small>
                                    Zum Beispiel ein externer Vater eines Wurfs. Er kann beim
                                    Wurf angezeigt werden, aber muss nicht in der eigenen
                                    Hundeübersicht erscheinen.
                                </small>
                            </span>
                        </label>
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="badges">
                            Badges
                        </label>
                        <textarea
                            id="badges"
                            value={badges}
                            onChange={(event) => setBadges(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "110px",
                                resize: "vertical",
                                lineHeight: 1.6,
                                fontFamily: "inherit",
                            }}
                            placeholder={"Eine Zeile pro Badge\nz. B. Patella 0/0 frei"}
                        />
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="details">
                            Details
                        </label>
                        <textarea
                            id="details"
                            value={details}
                            onChange={(event) => setDetails(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "140px",
                                resize: "vertical",
                                lineHeight: 1.6,
                                fontFamily: "inherit",
                            }}
                            placeholder={"Format: Label: Wert\nz. B. Wurftag: 14.08.2023\nVater: Athos"}
                        />
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="highlights">
                            Highlights
                        </label>
                        <textarea
                            id="highlights"
                            value={highlights}
                            onChange={(event) => setHighlights(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "110px",
                                resize: "vertical",
                                lineHeight: 1.6,
                                fontFamily: "inherit",
                            }}
                            placeholder={"Eine Zeile pro Highlight\nz. B. V1 Europasieger 2025"}
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <motion.button
                            type="submit"
                            disabled={isSaving}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                opacity: isSaving ? 0.7 : 1,
                            }}
                            whileHover={{
                                scale: isSaving ? 1 : 1.03,
                                backgroundColor: secondaryColor,
                            }}
                            whileTap={{ scale: isSaving ? 1 : 0.97 }}
                        >
                            {isSaving
                                ? "Wird gespeichert..."
                                : isEditing
                                    ? "Änderungen speichern"
                                    : "Hund erstellen"}
                        </motion.button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/dogs")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: "#777",
                            }}
                        >
                            Abbrechen
                        </button>
                    </div>
                </motion.form>
            </div>
        </main>
    );
}